<template>
    <BaseContainer>
        <span slot="menu">
            <v-app-bar app dark color="primary">
                <v-btn icon @click="onClickGoBack">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-spacer />
                <span style="text-transform: uppercase; font-weight: bold;">
                    Pedido #{{ order.id }}
                </span>
                <v-spacer />
                <div style="width: 36px;"></div>
            </v-app-bar>
        </span>

        <span slot="principal">
            <v-content class="pagamento-page">
                <v-container class="py-6 px-3" style="max-width: 640px;">

                    <!-- ===== PAGAMENTO APROVADO ===== -->
                    <v-card
                        v-if="status === 'approved'"
                        class="pa-6 pa-sm-8 text-center"
                        elevation="2"
                        style="border-radius: 16px;"
                    >
                        <v-avatar size="92" color="green lighten-5" class="mb-4">
                            <v-icon size="58" color="green darken-1">mdi-check-circle</v-icon>
                        </v-avatar>
                        <h2 class="text-h5 font-weight-bold mb-2">Pagamento aprovado!</h2>
                        <p class="grey--text text--darken-1 mb-6">
                            Seu pedido foi confirmado. Toque abaixo para enviar no WhatsApp e acompanhar.
                        </p>
                        <v-btn
                            color="#25D366"
                            dark
                            x-large
                            block
                            rounded
                            class="mb-3 text-none"
                            :loading="loading"
                            @click="sendWhatsapp"
                        >
                            <v-icon left>mdi-whatsapp</v-icon> Enviar no WhatsApp
                        </v-btn>
                        <v-btn color="primary" text block class="text-none" @click="goToCardapio">
                            Voltar ao cardápio
                        </v-btn>
                    </v-card>

                    <!-- ===== PIX RECUSADO NA CRIAÇÃO ===== -->
                    <v-card
                        v-else-if="pixRecusado"
                        class="pa-6 pa-sm-8 text-center"
                        elevation="2"
                        style="border-radius: 16px;"
                    >
                        <v-avatar size="92" color="red lighten-5" class="mb-4">
                            <v-icon size="56" color="red darken-1">mdi-qrcode-remove</v-icon>
                        </v-avatar>
                        <h2 class="text-h5 font-weight-bold mb-2">Não foi possível gerar o PIX</h2>
                        <p class="grey--text text--darken-1 mb-6">
                            A loja não conseguiu gerar o PIX deste pedido. Envie o pedido no WhatsApp para combinar o pagamento direto com a loja.
                        </p>
                        <v-btn
                            color="#25D366"
                            dark
                            x-large
                            block
                            rounded
                            class="mb-3 text-none"
                            :loading="loading"
                            @click="sendWhatsapp"
                        >
                            <v-icon left>mdi-whatsapp</v-icon> Enviar pedido no WhatsApp
                        </v-btn>
                        <v-btn text block class="text-none" @click="goToCardapio">
                            Voltar ao cardápio
                        </v-btn>
                    </v-card>

                    <!-- ===== PAGAMENTO RECUSADO (fallback) ===== -->
                    <v-card
                        v-else-if="isRejected && !retryCard"
                        class="pa-6 pa-sm-8 text-center"
                        elevation="2"
                        style="border-radius: 16px;"
                    >
                        <v-avatar size="92" color="red lighten-5" class="mb-4">
                            <v-icon size="56" color="red darken-1">mdi-credit-card-off-outline</v-icon>
                        </v-avatar>
                        <h2 class="text-h5 font-weight-bold mb-2">Pagamento não aprovado</h2>
                        <p class="grey--text text--darken-1 mb-6">{{ statusDetail }}</p>

                        <div class="text-caption grey--text text-uppercase mb-3">Como deseja continuar?</div>

                        <v-btn
                            color="primary"
                            x-large
                            block
                            rounded
                            class="mb-3 text-none"
                            @click="tentarOutroCartao"
                        >
                            <v-icon left>mdi-credit-card-outline</v-icon> Tentar com outro cartão
                        </v-btn>
                        <v-btn
                            color="primary"
                            outlined
                            large
                            block
                            rounded
                            class="mb-3 text-none"
                            :loading="loadingFallback === 'pix'"
                            @click="pagarComPix"
                        >
                            <v-icon left>mdi-qrcode</v-icon> Pagar com PIX
                        </v-btn>
                        <v-btn
                            v-if="permiteEntrega"
                            large
                            block
                            rounded
                            text
                            class="text-none"
                            :loading="loadingFallback === 'entrega'"
                            @click="pagarNaEntrega"
                        >
                            <v-icon left>mdi-cash</v-icon> Pagar na entrega
                        </v-btn>
                    </v-card>

                    <!-- ===== PENDENTE / PAGANDO ===== -->
                    <div v-else>
                        <QrCodePix
                            v-if="isPix && status === 'pending'"
                            v-model="pixPayment"
                            @copyCode="copyPix"
                        />
                        <Timer
                            v-if="isPix && status === 'pending'"
                            class="mt-4"
                            @trigger="onTimerTrigger"
                        />

                        <template v-if="(isCreditCard && status === 'pending') || retryCard">
                            <div v-if="isLoggedIn" class="text-caption grey--text text-center mb-3">
                                Olá, {{ usuario.name }} 👋
                            </div>
                            <v-alert v-else color="primary" text dense class="mb-3">
                                <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
                                    <span class="text-body-2">Já tem conta? Entre para usar seus cartões salvos.</span>
                                    <v-btn color="primary" small depressed class="text-none" @click="showLoginDialog = true">
                                        Entrar
                                    </v-btn>
                                </div>
                            </v-alert>

                            <CartaoCredito
                                v-model="order"
                                @pay="onCreditCardPayment"
                            />
                        </template>
                    </div>

                    <CreateAccount
                        v-if="showCreateAccountDialog"
                        v-model="order.whatsapp"
                        :ddi="order.whatsapp_ddi"
                        @create="onCreateAccount"
                    />

                    <Login
                        v-if="showLoginDialog"
                        v-model="order.whatsapp"
                        @login="onLogin"
                        @close="showLoginDialog = false"
                    />

                    <Challenge3DS
                        v-if="challenge3ds"
                        :external-resource-url="challenge3ds.external_resource_url"
                        :creq="challenge3ds.creq"
                        @verificar="onVerificarChallenge"
                        @cancelar="onCancelarChallenge"
                    />
                </v-container>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import Timer from './Timer';
import QrCodePix from './QrCodePix';
import CartaoCredito from './CartaoCredito';
import CreateAccount from '@/pages/auth/CreateAccount';
import Login from '@/pages/auth/Login';
import Challenge3DS from './Challenge3DS';
import { io } from 'socket.io-client';
import { mapState, mapMutations, mapActions } from 'vuex';

const FORMA_DINHEIRO = 1;
const FORMA_PIX = 3;

export default {
    name: 'Pagamento',

    components: {
        BaseContainer,
        Timer,
        QrCodePix,
        CartaoCredito,
        CreateAccount,
        Login,
        Challenge3DS,
    },

    data: () => ({
        pixPayment: null,
        hasAccount: false,
        order: {},
        emp: {},
        socket: null,
        loading: false,
        showCreateAccountDialog: false,
        showLoginDialog: false,
        challenge3ds: null, // débito 3DS: { external_resource_url, creq }
        // Fallback na recusa
        permiteEntrega: false, // futuramente virá de uma config do tenant
        retryCard: false,
        loadingFallback: false,
    }),

    computed: {
        ...mapState(['usuario']),

        isLoggedIn() {
            return !!this.usuario?.id;
        },

        status() {
            const pgto = this.ultimoPagamento;

            if (!pgto) {
                return 'pending';
            }

            const po = pgto.pagamento_online;
            const real = po.payment?.status;

            // 'pendente' é o status temporário do nosso wrapper (antes do webhook).
            // Nesse caso usamos o status imediato da resposta do MP (preference).
            if (real && real !== 'pendente') {
                return real;
            }

            return po.preference?.status || 'pending';
        },

        isRejected() {
            return this.status === 'rejected';
        },

        // PIX recusado já na criação é regra do Mercado Pago sobre a conta da loja:
        // gerar outro PIX ou tentar cartão não resolve, só a loja.
        pixRecusado() {
            const po = this.ultimoPagamento?.pagamento_online;

            return this.isRejected
                && (po?.metodo === 'pix' || po?.preference?.payment_method_id === 'pix');
        },

        statusDetail() {
            const po = this.ultimoPagamento?.pagamento_online;

            const detail = po?.preference?.status_detail || po?.payment?.status_detail;

            if (!detail || ['accredited', 'pending_waiting_transfer', 'pending_contingency'].includes(detail)) {
                return 'O pagamento foi recusado. Tente outro cartão ou outra forma de pagamento.';
            }

            return this.rejectionMessages[detail]
                || 'O pagamento foi recusado. Tente outro cartão ou outra forma de pagamento.';
        },

        rejectionMessages() {
            return {
                cc_rejected_insufficient_amount: 'Saldo ou limite insuficiente no cartão.',
                cc_rejected_bad_filled_card_number: 'Número do cartão inválido. Confira e tente novamente.',
                cc_rejected_bad_filled_date: 'Data de validade inválida.',
                cc_rejected_bad_filled_security_code: 'Código de segurança (CVV) inválido.',
                cc_rejected_bad_filled_other: 'Dados do cartão inválidos. Confira e tente novamente.',
                cc_rejected_call_for_authorize: 'Você precisa autorizar este pagamento com o banco emissor do cartão.',
                cc_rejected_card_disabled: 'Cartão desabilitado. Ligue para o banco para ativá-lo.',
                cc_rejected_card_error: 'Não foi possível processar o cartão. Tente novamente.',
                cc_rejected_duplicated_payment: 'Pagamento duplicado. Se precisar pagar de novo, use outro cartão.',
                cc_rejected_high_risk: 'Pagamento recusado por segurança. Tente outro meio de pagamento.',
                cc_rejected_invalid_installments: 'O cartão não aceita esse número de parcelas.',
                cc_rejected_max_attempts: 'Limite de tentativas atingido. Tente outro cartão.',
                cc_rejected_blacklist: 'Pagamento recusado. Tente outro cartão.',
                cc_rejected_other_reason: 'O banco recusou o pagamento. Tente outro cartão.',
            };
        },

        isPix() {
            return this.order.forma_pagamento == FORMA_PIX;
        },

        isCreditCard() {
            return [2, 4].includes(this.order.forma_pagamento);
        },

        // Pagamento online mais recente (maior id) — robusto contra a ordem do array,
        // essencial quando há mais de uma tentativa (ex.: cartão recusado + PIX novo).
        ultimoPagamento() {
            const pgtos = (this.order.pagamentos || []).filter(e => e.pagamento_online);

            if (!pgtos.length) {
                return null;
            }

            return pgtos.reduce((a, b) => ((b.id || 0) > (a.id || 0) ? b : a));
        },
    },

    async mounted() {
        this.restoreAuth();
        await this.findOrder();
        this.ws();
    },

    methods: {
        ...mapMutations({
            setCardPaymentLoading: 'setCardPaymentLoading',
        }),

        ...mapActions([
            'userAuthenticate',
        ]),

        ws() {
            const { uuid } = this.order;
            const isLocalHost = document.URL.indexOf('localhost') !== -1;

            if (this.socket || this.status != 'pending') {
                return;
            }

            this.socket = isLocalHost ? io('http://localhost:3000') : io();

            this.socket.on('connect', () => {
                uuid && this.socket.emit('order-payment', uuid);
            });

            this.socket.on('payment', () => {
                location.reload();
            });
        },

        copyPix() {
            const { qr_code } = this.pixPayment.point_of_interaction.transaction_data;
            navigator.clipboard.writeText(qr_code).then(
                () => this.notify('Código copiado!'),
                () => this.notify('Falha ao copiar!', 'warning'),
            );
        },

        async findOrder() {
            await this.$http.get(`pedido/${this.$route.params.pedUuid}`).then(resp => {
                const data = resp.data.data;
                const { pedido, empresa, hasAccount } = data;

                if (data.type == 'warning') {
                    this.notify(data.msg, 'warning');
                    return;
                }

                this.order = pedido;
                this.emp = empresa;
                this.hasAccount = hasAccount;

                window.$mpPublicKey = empresa.mpPublicKey;

                this.setPayment();
            })
            .catch((e) => {
                console.log(e);
                this.notify('Não foi possivel concluir a operação', 'warning');
            });
        },

        setPayment() {
            const ultimo = this.ultimoPagamento;

            if (ultimo) {
                this.pixPayment = ultimo.pagamento_online.preference;
            }
        },

        async onCreditCardPayment(data) {
            const pedUuid = this.$route.params.pedUuid;

            await this.$http.post(`pedido/${pedUuid}/pagamento`, data).then(resp => {
                if (resp.data.type == 'warning') {
                    this.notify(resp.data.msg, 'warning');
                    return;
                }

                // Débito 3DS: abre o challenge do banco. O resultado chega pelo
                // socket 'payment' (reload) ou pelo botão "verificar".
                if (resp.data.challenge) {
                    this.challenge3ds = resp.data.challenge;
                    return;
                }

                this.retryCard = false;
                this.findOrder();
            })
            .catch((e) => {
                console.log(e);
                this.notify('Não foi possivel concluir a operação', 'warning');
            })
            .finally(() => {
                this.setCardPaymentLoading(false);
            });
        },

        async onVerificarChallenge() {
            await this.findOrder();
            if (this.status !== 'pending') {
                this.challenge3ds = null; // a tela já reflete aprovado/recusado
            } else {
                this.notify('Pagamento ainda em processamento. Aguarde alguns instantes.', 'warning');
            }
        },

        onCancelarChallenge() {
            // Cliente desistiu do challenge: fecha e reabre o form pra tentar outro
            // cartão/método. O pedido segue aguardando_pagamento.
            this.challenge3ds = null;
            this.retryCard = true;
        },

        tentarOutroCartao() {
            this.retryCard = true;
        },

        async pagarComPix() {
            this.loadingFallback = 'pix';
            const pedUuid = this.$route.params.pedUuid;

            await this.$http.post(`pedido/${pedUuid}/forma-pagamento`, { forma_pagamento: FORMA_PIX }).then(resp => {
                if (resp.data.type !== 'success') {
                    this.notify(resp.data.msg || 'Não foi possível trocar para PIX', 'warning');
                    return;
                }

                this.retryCard = false;
                this.findOrder(); // recarrega com a nova forma + o PIX gerado → mostra o QR
            })
            .catch(() => {
                this.notify('Não foi possivel concluir a operação', 'warning');
            })
            .finally(() => (this.loadingFallback = false));
        },

        async pagarNaEntrega() {
            this.loadingFallback = 'entrega';
            const pedUuid = this.$route.params.pedUuid;

            await this.$http.post(`pedido/${pedUuid}/forma-pagamento`, { forma_pagamento: FORMA_DINHEIRO }).then(resp => {
                if (resp.data.type !== 'success') {
                    this.notify(resp.data.msg || 'Não foi possível concluir', 'warning');
                    return;
                }

                this.notify('Pedido confirmado! Pague na entrega.');
                this.$router.push(`/${this.$route.params.emp}/acompanhe-seu-pedido/${this.order.uuid}`).catch(() => {});
            })
            .catch(() => {
                this.notify('Não foi possivel concluir a operação', 'warning');
            })
            .finally(() => (this.loadingFallback = false));
        },

        async sendWhatsapp() {
            this.loading = true;

            await this.$http.get(`pedido/${this.emp.uuid}/${this.order.uuid}`).then(resp => {
                const { phone, text } = resp.data.data;

                this.phone = phone;
                this.text = window.encodeURIComponent(text);
                this.loading = false;

                window.open(`whatsapp://send?phone=${this.phone}&text=${this.text}`);
            });
        },

        onTimerTrigger() {
            this.findOrder();
        },

        async onClickGoBack() {
            // Já pago, ou PIX recusado (não há o que pagar aqui): volta direto pro cardápio.
            if (this.status === 'approved' || this.pixRecusado) {
                this.goToCardapio();
                return;
            }

            // Pendente: confirma a saída de forma amigável (não cancela o pedido).
            const sair = await this.$root.$confirm(
                'Sair do pagamento?',
                'Seu pedido ficará aguardando pagamento — você pode voltar e pagar depois.',
                { color: 'primary' },
            );

            if (sair) {
                this.goToCardapio();
            }
        },

        goToCardapio() {
            this.$router.push(`/${this.$route.params.emp}`).catch(() => {});
        },

        // Restaura a sessão do consumidor (token salvo) e carrega os cartões do cofre.
        restoreAuth() {
            const emp = this.$http.defaults.headers.common['emp'];
            const token = emp ? localStorage.getItem(`${emp}-access-token`) : null;

            if (token) {
                this.$http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                this.userAuthenticate();
            }
        },

        onCreateAccount() {
            this.showCreateAccountDialog = false;
            this.userAuthenticate();
            this.notify('Conta criada com sucesso');
        },

        onLogin() {
            this.showLoginDialog = false;
            this.notify('Login efetuado com sucesso');
        },
    },
}
</script>

<style scoped>
.pagamento-page { background: #f4f5f7; }
/* No tema escuro o texto do Vuetify é branco: o fundo segue o do app. */
.theme--dark .pagamento-page { background: transparent; }
</style>
